/**
 * Copy text that is not known yet — a URL the server is still minting — without
 * losing the user gesture.
 *
 * WebKit (Safari, every iOS browser) honours a clipboard write only while the
 * click that asked for it is still "active"; a `writeText` that runs after an
 * `await fetch` is outside that window and rejects with NotAllowedError. The
 * Async Clipboard API's answer is a `ClipboardItem` whose value is a PROMISE:
 * the write STARTS inside the gesture, the bytes arrive later. Chromium accepts
 * the same shape.
 *
 * Returns `true` when the eager write took the text, `false` when the browser
 * has no `ClipboardItem`/`write` (then the caller falls back to `copyToClipboard`
 * once the text is known) or refused. A rejected `text` promise resolves to
 * `false` as well — the caller learns about the failure from its own await, not
 * from here.
 */
export function copyTextFromPromise(text: Promise<string>): Promise<boolean> {
  if (
    typeof navigator === "undefined" ||
    typeof ClipboardItem === "undefined" ||
    typeof navigator.clipboard?.write !== "function"
  ) {
    return Promise.resolve(false);
  }
  try {
    // Derived promise, handled here as well: when the engine never consumes
    // it (constructor throws on an old engine, or `write` rejects early on a
    // permission/focus check) a rejected mint would otherwise surface as an
    // unhandled rejection — a second Sentry event for one failure the caller
    // already reports from its own await of `text`.
    const blob = text.then((t) => new Blob([t], { type: "text/plain" }));
    blob.catch(() => {});
    const item = new ClipboardItem({ "text/plain": blob });
    return navigator.clipboard.write([item]).then(
      () => true,
      () => false,
    );
  } catch {
    return Promise.resolve(false);
  }
}
